import { useState, useEffect } from 'react';
import Header from './components/Header';
import { DateNavigation } from './components/DateNavigation';
import { SleepEntryForm } from './components/SleepEntryForm';
import { SleepEntriesList } from './components/SleepEntriesList';
import { SleepStats } from './components/SleepStats';
import { SleepPatterns } from './components/SleepPatterns';
import { FloatingActionButton } from './components/FloatingActionButton';
import { Notification } from './components/Notification';
import { ChildSelector } from './components/ChildSelector';
import { DataManager } from './components/DataManager';
import { ThemeSelector } from './components/ThemeSelector';
import { useSleepData } from './hooks/useSleepData';
import { useTheme } from './hooks/useTheme';
import { sleepDB, initializeBackupSystem } from './database/db';
import { format, startOfDay } from 'date-fns';
import BottomNav from './components/BottomNav';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';

function MainApp() {
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [selectedChild, setSelectedChild] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [notification, setNotification] = useState(null);
  const { isDark, toggleTheme, currentTheme, setCurrentTheme } = useTheme();
  const [showAddChildForm, setShowAddChildForm] = useState(false);
  
  const { 
    entries, 
    children,
    entriesLoading, 
    childrenLoading,
    error, 
    addEntry, 
    updateEntry, 
    deleteEntry,
    refreshEntries,
    addChild,
    updateChild,
    deleteChild
  } = useSleepData(selectedDate, selectedChild?.id);

  const location = useLocation();
  const navigate = useNavigate();
  const currentView = location.pathname.replace('/', '') || 'tracker';

  // Initialize backup system and load children
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize backup system first
        await initializeBackupSystem();
        
        // Set default theme if none is set
        if (!currentTheme) {
          setCurrentTheme('moon-stars');
        }
      } catch (error) {
        console.error('Error during app initialization:', error);
        showNotification('Fehler beim Initialisieren der App', 'error');
      }
    };

    initializeApp();
  }, [currentTheme, setCurrentTheme]);

  // Update selected child when children change
  useEffect(() => {
    if (children.length > 0 && !selectedChild) {
      setSelectedChild(children[0]);
    }
  }, [children, selectedChild]);

  // Add new child
  const handleAddChild = async (childData) => {
    try {
      const childId = await addChild(childData);
      showNotification('Kind erfolgreich hinzugefügt', 'success');
      return childId;
    } catch (error) {
      console.error('Error adding child:', error);
      showNotification('Fehler beim Hinzufügen des Kindes', 'error');
      throw error;
    }
  };

  // Edit child
  const handleEditChild = async (childId, updates) => {
    try {
      await updateChild(childId, updates);
      
      // Update selected child if it's the one being edited
      if (selectedChild?.id === childId) {
        setSelectedChild(prev => ({ ...prev, ...updates }));
      }
      
      showNotification('Kind erfolgreich aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating child:', error);
      showNotification('Fehler beim Aktualisieren des Kindes', 'error');
      throw error;
    }
  };

  // Delete child
  const handleDeleteChild = async (childId) => {
    try {
      await deleteChild(childId);
      
      // Clear selected child if it was deleted
      if (selectedChild?.id === childId) {
        setSelectedChild(null);
      }
      
      showNotification('Kind erfolgreich gelöscht', 'success');
    } catch (error) {
      console.error('Error deleting child:', error);
      showNotification('Fehler beim Löschen des Kindes', 'error');
      throw error;
    }
  };

  // Select child
  const handleSelectChild = (child) => {
    setSelectedChild(child);
    navigate('/tracker');
  };

  // Handle sleep entry save
  const handleSaveEntry = async (entryData) => {
    try {
      if (editingEntry) {
        await updateEntry(editingEntry.id, entryData);
        showNotification('Eintrag erfolgreich aktualisiert', 'success');
      } else {
        await addEntry(entryData);
        showNotification('Eintrag erfolgreich hinzugefügt', 'success');
      }
      setEditingEntry(null);
    } catch (error) {
      console.error('Error saving entry:', error);
      showNotification('Fehler beim Speichern des Eintrags', 'error');
    }
  };

  // Handle entry edit
  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  // Handle entry delete
  const handleDeleteEntry = async (entryId) => {
    try {
      await deleteEntry(entryId);
      showNotification('Eintrag erfolgreich gelöscht', 'success');
    } catch (error) {
      console.error('Error deleting entry:', error);
      showNotification('Fehler beim Löschen des Eintrags', 'error');
    }
  };

  // Update sleep patterns
  const handleUpdatePatterns = async (childId, patternType, averageTime, confidence) => {
    try {
      const timezone = selectedChild?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      await sleepDB.updateSleepPattern(childId, patternType, averageTime, confidence, timezone);
      showNotification('Schlafmuster aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating patterns:', error);
      showNotification('Fehler beim Aktualisieren der Muster', 'error');
    }
  };

  // Handle timezone change
  const handleTimezoneChange = async (newTimezone, changeInfo) => {
    try {
      await sleepDB.updateChildTimezone(selectedChild.id, newTimezone, changeInfo);
      await loadChildren();
      showNotification('Zeitzone aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating timezone:', error);
      showNotification('Fehler beim Aktualisieren der Zeitzone', 'error');
    }
  };

  // Show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Handle theme change
  const handleThemeChange = (themeId) => {
    setCurrentTheme(themeId);
    if (selectedChild) {
      handleEditChild(selectedChild.id, { theme: themeId });
    }
  };

  // Get current theme class
  const getThemeClass = () => {
    const childTheme = selectedChild?.theme || currentTheme || 'moon-stars';
    return `theme-${childTheme}`;
  };

  return (
    <div className={`min-h-screen ${getThemeClass()}`}>
      <Header selectedChild={selectedChild} />
      <div className="main-container">
        <Routes>
          <Route path="/children" element={
            <div className="space-y-4 pt-2">
              <ChildSelector
                children={children}
                selectedChild={selectedChild}
                onSelectChild={handleSelectChild}
                onAddChild={async (data) => {
                  await handleAddChild(data);
                  setShowAddChildForm(false);
                }}
                onEditChild={handleEditChild}
                onDeleteChild={handleDeleteChild}
                loading={childrenLoading}
                showAddForm={showAddChildForm}
                setShowAddForm={setShowAddChildForm}
              />
            </div>
          } />
          <Route path="/data" element={
            <div className="space-y-4 pt-2">
              <DataManager
                onExport={async () => {
                  try {
                    return await sleepDB.exportData();
                  } catch (error) {
                    console.error('Error exporting data:', error);
                    showNotification('Fehler beim Exportieren', 'error');
                    throw error;
                  }
                }}
                onImport={async (data) => {
                  try {
                    await sleepDB.importData(data);
                    showNotification('Daten erfolgreich importiert', 'success');
                  } catch (error) {
                    console.error('Error importing data:', error);
                    showNotification('Fehler beim Importieren', 'error');
                    throw error;
                  }
                }}
                onBackup={async () => {
                  try {
                    await sleepDB.createBackup();
                    showNotification('Backup erfolgreich erstellt', 'success');
                  } catch (error) {
                    console.error('Error creating backup:', error);
                    showNotification('Fehler beim Erstellen des Backups', 'error');
                  }
                }}
                onRestore={async () => {
                  try {
                    const success = await sleepDB.restoreBackup();
                    if (success) {
                      showNotification('Backup erfolgreich wiederhergestellt', 'success');
                    } else {
                      showNotification('Kein Backup gefunden oder Fehler beim Wiederherstellen', 'warning');
                    }
                  } catch (error) {
                    console.error('Error restoring backup:', error);
                    showNotification('Fehler beim Wiederherstellen des Backups', 'error');
                  }
                }}
                onReset={async () => {
                  try {
                    await sleepDB.resetDatabase();
                    setSelectedChild(null);
                    showNotification('Datenbank erfolgreich zurückgesetzt', 'success');
                  } catch (error) {
                    console.error('Error resetting database:', error);
                    showNotification('Fehler beim Zurücksetzen der Datenbank', 'error');
                  }
                }}
              />
            </div>
          } />
          <Route path="/tracker" element={
            selectedChild ? (
              <div className="space-y-4 pt-2">
                {/* Child Info - More Prominent */}
                <div className="card">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="flex-1">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1">
                        {selectedChild.name}
                      </h2>
                      {selectedChild.birthDate && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {format(new Date(selectedChild.birthDate), 'dd.MM.yyyy')} geboren
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      <ThemeSelector
                        selectedTheme={selectedChild?.theme || currentTheme}
                        onThemeChange={handleThemeChange}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Date Navigation */}
                <div className="card">
                  <DateNavigation
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                  />
                </div>
                
                {/* Sleep Statistics */}
                <div className="card">
                  <SleepStats entries={entries} childTimezone={selectedChild?.timezone} />
                </div>
                
                {/* Sleep Patterns */}
                <div className="card">
                  <SleepPatterns
                    entries={entries}
                    child={selectedChild}
                    onUpdatePatterns={handleUpdatePatterns}
                    loading={entriesLoading}
                  />
                </div>
                
                {/* Sleep Entries List */}
                <div className="card">
                  <SleepEntriesList
                    entries={entries}
                    loading={entriesLoading}
                    onEdit={handleEditEntry}
                    onDelete={handleDeleteEntry}
                    childTimezone={selectedChild?.timezone}
                  />
                </div>
              </div>
            ) : (
              <div className="card text-center py-8 mt-2">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Kein Kind ausgewählt
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Wähle ein Kind aus oder füge ein neues hinzu, um mit dem Schlaf-Tracking zu beginnen.
                </p>
                <button
                  onClick={() => navigate('/children')}
                  className="btn-primary"
                >
                  Kinder verwalten
                </button>
              </div>
            )
          } />
          <Route path="*" element={<Navigate to="/tracker" />} />
        </Routes>
        
        {/* Floating Action Button */}
        {currentView === 'tracker' && selectedChild && (
          <FloatingActionButton
            onClick={() => {
              setEditingEntry(null);
              setShowForm(true);
            }}
          />
        )}
        
        {currentView === 'children' && !showAddChildForm && (
          <FloatingActionButton
            onClick={() => setShowAddChildForm(true)}
          />
        )}
        
        {/* Sleep Entry Form */}
        <SleepEntryForm
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingEntry(null);
          }}
          onSave={handleSaveEntry}
          selectedDate={selectedDate}
          editEntry={editingEntry}
          childId={selectedChild?.id}
          childTimezone={selectedChild?.timezone}
        />
        
        {/* Notification */}
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}
      </div>
      
      {/* Bottom Navigation */}
      <BottomNav currentView={currentView} onViewChange={view => navigate('/' + view)} />
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <MainApp />
    </Router>
  );
} 